/**
 * Crosshair tooltip (Phase 3, §6.4) — SPINE. A self-contained SVG tooltip so it
 * lives inside the chart's `<svg>` (no HTML-overlay coordinate juggling). It
 * flips to the other side of the anchor near the right edge and clamps within
 * the plot. Values are pre-formatted de-DE by the caller.
 */

export interface TooltipRow {
  label: string;
  value: string;
  color?: string;
}

export interface TooltipProps {
  /** Anchor x (the hovered category centre). */
  x: number;
  /** Anchor y (near the top of the plot). */
  y: number;
  title: string;
  rows: TooltipRow[];
  /** Full chart width, for edge flipping. */
  chartWidth: number;
}

const PAD = 8;
const LINE_H = 16;
const CHAR_W = 6.6;
const SWATCH = 8;

export function Tooltip({ x, y, title, rows, chartWidth }: TooltipProps) {
  const rowText = (r: TooltipRow) => `${r.label}  ${r.value}`;
  const longest = Math.max(
    title.length,
    ...rows.map((r) => rowText(r).length + (r.color ? 2 : 0)),
  );
  const width = Math.min(Math.max(longest * CHAR_W + PAD * 2, 96), 260);
  const height = PAD * 2 + LINE_H + rows.length * LINE_H;

  const flip = x + 12 + width > chartWidth;
  const boxX = Math.max(4, Math.min(flip ? x - 12 - width : x + 12, chartWidth - width - 4));
  const boxY = Math.max(4, y);

  return (
    <g pointerEvents="none" transform={`translate(${boxX}, ${boxY})`}>
      <rect
        width={width}
        height={height}
        rx={8}
        ry={8}
        fill="var(--viz-tooltip-bg)"
        stroke="var(--viz-tooltip-border)"
        strokeWidth={1}
        opacity={0.98}
        style={{ filter: 'drop-shadow(0 1px 2px rgb(0 0 0 / 0.05))' }}
      />
      <text
        x={PAD}
        y={PAD + 11}
        fontSize={11}
        fontWeight={600}
        fill="var(--viz-tooltip-fg)"
      >
        {title}
      </text>
      {rows.map((r, i) => {
        const rowY = PAD + LINE_H + i * LINE_H;
        return (
          <g key={`${r.label}-${i}`} transform={`translate(0, ${rowY})`}>
            {r.color ? (
              <rect
                x={PAD}
                y={2}
                width={SWATCH}
                height={SWATCH}
                rx={2}
                fill={r.color}
              />
            ) : null}
            <text
              x={r.color ? PAD + SWATCH + 4 : PAD}
              y={10}
              fontSize={11}
              fill="var(--viz-tooltip-fg)"
            >
              {r.label}
            </text>
            <text
              x={width - PAD}
              y={10}
              textAnchor="end"
              fontSize={11}
              fontWeight={600}
              fill="var(--viz-tooltip-fg)"
            >
              {r.value}
            </text>
          </g>
        );
      })}
    </g>
  );
}
